import type { Key, Obj } from "@/v2-common/types"

type ClampOptions = {
  min?: number
  max?: number
}

/**
 * Ensures a number stays within a given min/max range, or simply
 * the min or the max when both are not specified
 */
export function clamp(value: number, options: ClampOptions) {
  const { min, max } = options
  if (typeof min === "number" && value < min) value = min
  if (typeof max === "number" && value > max) value = max
  return value
}

type RangeOptions = {
  min?: number
  max?: number
}

/**
 * Create an array with empty elements up to a given count.
 * Specify options to clamp length
 */
export function range(count: number, options?: RangeOptions) {
  if (options) count = clamp(count, options)
  return Array.from<never>({ length: count })
}

function assertObj(obj: unknown): obj is object {
  return !!obj && typeof obj === "object"
}

/**
 * Safely parses a JSON string into an object.
 *
 * If the value is a string, it attempts to parse it as JSON.
 * If the value is an object with keys, it returns the object.
 * In all other cases or if parsing fails, it returns `null`.
 *
 * @template T - The expected return type. Default is an object.
 * @param {unknown} val - The value to be parsed.
 * @returns {T} - Parsed JSON value, the original object if it's
 *                non-empty, or `null`.
 */
export function safeJSONParse<T = Obj>(val: unknown): T | null {
  try {
    if (typeof val === "string") {
      return JSON.parse(val)
    }
    if (assertObj(val)) {
      const z = Object.keys(val)
      return z.length > 0 ? (val as T) : null
    }
    return null
  } catch {
    return null
  }
}

export function sortByKeyAndNumber<T extends Obj>(key: Key<T>) {
  return (a: T, b: T) => {
    if (!Number.isInteger(a[key])) return 1
    if (!Number.isInteger(b[key])) return -1
    if (!Number.isInteger(a[key]) && !Number.isInteger(b[key])) return 0
    return (a[key] as number) - (b[key] as number)
  }
}

export function calcMedian(values: number[]) {
  values.sort((a, b) => a - b)
  const half = Math.floor(values.length / 2)
  return values.length % 2
    ? values[half]
    : (values[half - 1] + values[half]) / 2
}
